import { BaseHTMLAttributes, GraphChildProps, GraphScale, useGraphCtx } from '@mi-front/core';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { StreamElWrapper } from './elements';

interface PassedProps extends BaseHTMLAttributes {
  /**
   * timestamp inside the graph range, if not given the
   * element will be placed automatically with css
   */
  readonly ts?: number;

  /**
   * position of the element relative to container
   * - top: style.top set to 0
   * - bottom: style.bottom set to 0
   * - center: style.top set to 50%
   */
  readonly position?: 'top' | 'center' | 'bottom';

  /**
   * if fullHeight = true, the element will take up
   * the entire height of the graph
   */
  readonly fullHeight?: boolean;

  /**
   * force z-index value
   */
  readonly zIndex?: number;

  /**
   * if true, disables any pointer events
   */
  readonly noEvents?: boolean;

  /**
   * offset on the x-axis relative to the
   * calculated from the timestamp
   */
  readonly offsetX?: number;

  /**
   * offset on the y-axis relative to the
   * position type given
   */
  readonly offsetY?: number;

  /**
   * if true, the element children will be placed at the left
   * of the component instead of right
   */
  readonly anchor?: 'left' | 'right';

  /**
   * if true, will check when the element overflows and change the
   * anchor position accordingly
   */
  readonly checkOverflow?: boolean;

  /**
   * when overflowing, this offset will be applied to the final position
   * useful when must not be 100% horizontally shifted
   */
  readonly overflowOffset?: number;

  readonly children?: React.ReactNode | ((scale: GraphScale) => React.ReactNode);
}

export type StreamElProps = PassedProps;

export interface StreamElContainerProps {
  readonly elProps?: StreamElProps;
}

type Props = GraphChildProps & PassedProps;

export function StreamElP(props: Props) {
  const {
    ts,
    className,
    scale,
    parentHeight,
    zIndex = 1,
    fullHeight = false,
    style,
    parentWidth,
    noEvents = false,
    position = 'top',
    offsetX = 0,
    offsetY = 0,
    anchor = 'left',
    children,
    overflowOffset = 0,
    checkOverflow = false,
  } = props;

  const [state, setState] = useState({
    isOverflowing: false,
  });

  const ref = useRef<HTMLDivElement | null>(null);

  const checkOverflowFunc = () => {
    if (!ref.current || !checkOverflow) {
      return;
    }

    let parent = ref.current.parentElement!;
    while (parent && !parent.className.includes('stream__wrapper')) {
      parent = parent.parentElement!;
    }

    if (!parent) {
      return;
    }

    const parentX = parent.getBoundingClientRect().x;
    let rightX = ref.current.getBoundingClientRect().right - parentX;

    if (state.isOverflowing) {
      rightX = rightX + offsetX! * 2 + ref.current.clientWidth;
    }

    setState({
      isOverflowing: rightX > parentWidth,
    });
  };

  useEffect(() => {
    checkOverflowFunc();
  }, [props]);

  return (
    <StreamElWrapper
      innerRef={(el: HTMLDivElement) => (ref.current = el)}
      className={classNames('stream-el', className)}
      noEvents={noEvents}
      fullHeight={fullHeight}
      height={parentHeight}
      offsetX={offsetX || 0}
      offsetY={offsetY || 0}
      style={style}
      zIndex={zIndex}
      left={ts ? scale(ts)! : 'auto'}
      position={position}
      anchor={state.isOverflowing ? 'right' : anchor}
      overflowOffset={overflowOffset}
    >
      {typeof children === 'function' ? (children as any)(scale) : children}
    </StreamElWrapper>
  );
}

/**
 * StreamEl is used as a container for any graph element
 */
export function StreamEl(p: Omit<Props, 'scale' | 'parentHeight' | 'parentWidth'>) {
  const { height, width, scale } = useGraphCtx();

  return <StreamElP parentHeight={height} parentWidth={width} scale={scale} {...p} />;
}
